import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useLaunchDarkly } from '@/components/App/LaunchDarklyGateway';
import { useAuthentication } from '@/hooks/user/useAuthentication';
import { useBranding } from '@/hooks/branding/useBranding';
import { QUERY_KEYS } from '@/resources/dataKeys';

type SignOutFn = () => Promise<void>;

type SignOutHandlerProps = {
  children: (signOut: SignOutFn, isBusy: boolean) => JSX.Element;
  onSuccess?: () => unknown;
};

export const SignOutHandler = ({
  children,
  onSuccess,
}: SignOutHandlerProps) => {
  const queryClient = useQueryClient();
  const [signOutBusy, setIsSignoutBusy] = useState(false);

  const {
    branding,
  } = useBranding();

  const {
    closeLDClient,
  } = useLaunchDarkly();

  const {
    user,
  } = useAuthentication();

  if (!user || !branding) {
    return null;
  }

  const signOutUser = async () => {
    setIsSignoutBusy(true);

    const heap = (window as any).heap;
    heap?.resetIdentity?.();
    heap?.stopTracking?.();

    // Clear LaunchDarkly context cache for current user
    queryClient.removeQueries({ queryKey: [QUERY_KEYS.secureFeatureFlagContext, user.uid] });

    await closeLDClient(); // Wait for client closure

    onSuccess?.();

    // Hand logout to Envoy: it clears the session cookie and lands back on /auth/signin.
    window.location.assign('/auth/signout');
  };

  return children(signOutUser, signOutBusy);
};
